import {useActorRef, useSelector} from '@xstate/react';
import React, {createContext, useContext, PropsWithChildren, useEffect} from 'react';
import {ActorRefFrom, ActorRefFromLogic} from 'xstate';

import {ReagentAssignmentGroup, REAGENTS_ATTRIBUTE_SENTINEL} from 'samples/plate-maps/PlateMapConstants';
import {createAssignmentGroupsForAttribute} from 'samples/plate-maps/PlateMapUtil';

import {
  DEFAULT_FILL_PLATE_MODAL_STATE,
  FillPlateModalReducerActionType,
  FillPlateModalState,
} from './machine/FillPlateModalConstants';
import {PlateModalMachine, PlateModalMachineEvents} from './machine/PlateModalMachine';

export type PlateModalMachineAction = (action: PlateModalMachineEvents) => void;

/**
 * Context for the PlateModal machine.
 * Provides access to the machine actor ref throughout the component tree.
 */
const PlateModalMachineCtx = createContext<ActorRefFromLogic<typeof PlateModalMachine> | null>(null);

type PlateModalMachineActor = ActorRefFrom<typeof PlateModalMachine>;

export function usePlateModalSelector<T = unknown>(
  selector: (snapshot: ReturnType<PlateModalMachineActor['getSnapshot']>) => T,
  compare?: (a: T, b: T) => boolean
): T {
  const actorRef = useContext(PlateModalMachineCtx)!;
  return useSelector(actorRef, selector, compare);
}

export const usePlateModalActorRef = (): PlateModalMachineActor => useContext(PlateModalMachineCtx)!;

/**
 * Hook that provides the same interface as the old useFillPlateModalReducer.
 *
 * @deprecated This hook is only used for `useFillPlateModalReducer-test.rtl.tsx`.
 * #TODO(BNCH-134322): Remove this hook after the tests are migrated.
 */
export function useFillPlateModalReducer(): [FillPlateModalState, PlateModalMachineAction] {
  const actorRef = usePlateModalActorRef();
  const state = usePlateModalSelector((snapshot) => snapshot.context);
  const dispatch: PlateModalMachineAction = (action) => {
    actorRef.send(action);
  };
  return [state, dispatch];
}

export interface PlateModalMachineProviderProps {
  initialState: Partial<FillPlateModalState>;
}

export function PlateModalMachineProvider({
  children,
  initialState,
}: PropsWithChildren<PlateModalMachineProviderProps>): JSX.Element {
  const actorRef = useActorRef(PlateModalMachine, {
    input: {
      ...DEFAULT_FILL_PLATE_MODAL_STATE,
      ...initialState,
      // Leave the plate map null for now, it will be populated with assignment groups in INITIALIZE_PLATE_MAP
      plateMap: null,
    },
  });

  useEffect(() => {
    const fetchInitialAssignmentGroups = async (): Promise<void> => {
      if (initialState.plateMap != null) {
        const assignmentGroups = (await createAssignmentGroupsForAttribute(
          initialState.plateMap,
          REAGENTS_ATTRIBUTE_SENTINEL
        )) as {[id: string]: ReagentAssignmentGroup};

        actorRef.send({
          type: FillPlateModalReducerActionType.INITIALIZE_PLATE_MAP,
          plateMap: initialState.plateMap,
          assignmentGroups,
        });
      }
    };

    fetchInitialAssignmentGroups();
  }, [initialState.plateMap, actorRef]);

  return <PlateModalMachineCtx.Provider value={actorRef}>{children}</PlateModalMachineCtx.Provider>;
}
